'use client';

import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { paths } from '@/constants/paths';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import useUser from '@/ui/providers/userProvider';
import { NukiDevicesProvider } from '@/ui/providers/nukiDevicesProvider';
import { buildTrackingId } from '@/utils/tracking-helper';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { ClubRegistrationTile } from './types/ClubRegistrationTile';
import EmailForm from './components/EmailForm/email-form';
import { NameForm } from './components/name-form/name-form';
import { LoggedInForm } from './components/logged-in-form/logged-in-form';
import Button from '../../shared/Button';
import { ScreenToShowState } from './types/ScreenToShowState';
import { useClubRegistrationHistory } from './hooks/useClubRegistrationHistory';

// The landing page spacing, and a compact one for a dialog.
const LAYOUT = {
  page: {
    container: 'min-h-[430px]',
    containerId: 'club-registration',
    screen: 'my-10 space-y-8 sm:my-12 sm:space-y-12',
    formScreen: 'my-10 space-y-4 sm:my-12 sm:space-y-12',
    title: 'mb-4 text-2xl lg:text-4xl',
    titleTabIndex: undefined,
    subtitle: 'my-6 text-lg',
  },
  dialog: {
    container: 'my-0 px-6 py-8 sm:my-0 sm:px-10 lg:my-0',
    containerId: undefined,
    screen: 'space-y-6',
    formScreen: 'space-y-8',
    title: 'mb-2 text-2xl sm:text-3xl',
    titleTabIndex: -1,
    subtitle: 'text-base',
  },
};

// In a dialog the submitted form is gone, so a screen reader would otherwise be left on nothing.
function useFocusTitleOnScreenChange(screenToShow: ScreenToShowState, isEnabled: boolean) {
  const titleRef = useRef<HTMLElement>(null);
  const shownScreenRef = useRef(screenToShow);
  useEffect(() => {
    if (!isEnabled || shownScreenRef.current === screenToShow) {
      return;
    }

    shownScreenRef.current = screenToShow;
    titleRef.current?.focus({ preventScroll: true });
  }, [isEnabled, screenToShow]);

  return titleRef;
}

export default function ClubRegistration({
  isInstallationCheck = false,
  enableHistory = false,
  staticContent,
  tile,
  onEmailSubmit,
  onCreateNewAccount,
  onExistingUserJoinClub,
  onWelcomeScreenAction,
  shouldReloadAfterWelcomeScreenAction = true,
  isInDialog = false,
  titleAs: TitleTag = 'p',
  nag,
  trackingPrefix,
}: Readonly<{
  isInstallationCheck?: boolean;
  enableHistory?: boolean;
  staticContent?: ClubTileStaticContentTexts;
  tile?: ClubRegistrationTile;
  onEmailSubmit?: (email: string) => void;
  onCreateNewAccount?: (firstName: string, lastName: string) => void;
  onExistingUserJoinClub?: () => void;
  onWelcomeScreenAction?: () => void;
  shouldReloadAfterWelcomeScreenAction?: boolean;
  // Compact layout, and focus follows the title when the screen changes under the user.
  isInDialog?: boolean;
  // Lets a dialog label itself with the visible title, e.g. Headless UI's Dialog.Title.
  titleAs?: React.ElementType;
  // Where the signup came from, for places that cannot set it through the page URL.
  nag?: string;
  trackingPrefix?: string;
}>) {
  const t = useTranslations('ClubRegistration');
  const { localizedLink } = useLocaleLink();
  const router = useRouter();
  const { customer, isInitialized } = useUser();
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [existingCustomerEmail, setExistingCustomerEmail] = useState(customer?.email ?? '');
  // Lazy init: on a soft-navigation mount the session is often already resolved, so
  // computing the screen here avoids painting the email form for one frame before
  // the effect below corrects it.
  const [screenToShow, setScreenToShow] = useState<ScreenToShowState>(() => {
    try {
      if (customer?.isClubMember()) {
        return ScreenToShowState.WELCOME;
      }

      if (customer?.email) {
        return ScreenToShowState.LOGGED_IN;
      }
    } catch (error) {
      // Same rationale as the effect below: treat model errors as "not logged in".
    }

    return ScreenToShowState.INITIAL;
  });

  // Custom hook to optionally keep screen navigation in history and handle URL synchronization
  useClubRegistrationHistory(screenToShow, setScreenToShow, enableHistory);

  const titleRef = useFocusTitleOnScreenChange(screenToShow, isInDialog);

  const initialScreenChunk = tile?.['initial-screen']?.chunks?.[0];
  const initialTitle = extractComponentFromChunk(initialScreenChunk, 'title', 'RichTextContent');
  const initialSubtitle = extractComponentFromChunk(
    initialScreenChunk,
    'subtitle',
    'RichTextContent',
  );
  const welcomeScreenChunk = tile?.['welcome-screen']?.chunks?.[0];
  const welcomeTitle = extractComponentFromChunk(welcomeScreenChunk, 'title', 'RichTextContent');
  const welcomeSubtitle = extractComponentFromChunk(
    welcomeScreenChunk,
    'subtitle',
    'RichTextContent',
  );
  useEffect(() => {
    async function checkIfUserIsClubMember() {
      try {
        if (customer?.isClubMember()) {
          setScreenToShow(ScreenToShowState.WELCOME);
        } else if (customer?.email) {
          setExistingCustomerEmail(customer.email);
          // Only promote from INITIAL: a late cache refresh (e.g. right after joining,
          // before the backend write is readable) must never pull the user back out of
          // WELCOME or NEW_CUSTOMER.
          setScreenToShow((current) => {
            if (current !== ScreenToShowState.INITIAL) {
              return current;
            }

            return ScreenToShowState.LOGGED_IN;
          });
        }
      } catch (error) {
        // Ignoring error since it means user is not logged in
      }
    }
    checkIfUserIsClubMember();
  }, [customer]);

  const onWelcomeScreenButtonClick = () => {
    if (onWelcomeScreenAction) {
      onWelcomeScreenAction();
      // The my-account club page and the IC survey rely on the legacy reload-after-action
      // behavior; the club join modal navigates or closes itself instead.
      if (shouldReloadAfterWelcomeScreenAction) {
        globalThis.location.reload();
      }

      return;
    }

    // Soft navigation is safe here: the join forms refresh the client-side
    // session/customer cache right after a successful signup, so it is no longer
    // stale by the time this button is clicked.
    router.push(localizedLink(paths.myAccount));
  };

  const welcomeButtonText = staticContent
    ? staticContent.welcomeMemberScreen.buttonText
    : t('My Account');
  const welcomeButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'welcome-screen',
    welcomeButtonText,
  );

  const layout = isInDialog ? LAYOUT.dialog : LAYOUT.page;
  const titleClassName = clsx('font-semibold focus:outline-none', layout.title);
  const subtitleClassName = clsx('text-center', layout.subtitle);

  return (
    <NukiDevicesProvider>
      <div
        className={twJoin(
          'flex w-full',
          !isInstallationCheck && 'bg-neutral-5',
          isInstallationCheck && 'mx-auto justify-center',
        )}
      >
        <Container className={layout.container} id={layout.containerId}>
          {screenToShow === ScreenToShowState.WELCOME ? (
            <div className={clsx('mx-auto max-w-2xl', layout.screen)}>
              <div className="space-y-2 text-center sm:space-y-3">
                {staticContent ? (
                  <>
                    <TitleTag
                      ref={titleRef}
                      tabIndex={layout.titleTabIndex}
                      className={titleClassName}
                    >
                      {formatPunctuation(staticContent.welcomeMemberScreen.title)}
                    </TitleTag>
                    <p className={subtitleClassName}>
                      {staticContent.welcomeMemberScreen.subtitle}
                    </p>
                  </>
                ) : (
                  <>
                    <RichTextRenderer blocks={welcomeTitle} className="text-3xl sm:text-4xl" />
                    <RichTextRenderer blocks={welcomeSubtitle} className="text-md sm:text-2xl" />
                  </>
                )}
              </div>
              <Button
                onClick={onWelcomeScreenButtonClick}
                className="mx-auto w-full max-w-sm"
                data-tile-element-id={welcomeButtonTrackingId}
              >
                {welcomeButtonText}
              </Button>
            </div>
          ) : (
            <div className={clsx('mx-auto max-w-2xl', layout.formScreen)}>
              <div className="space-y-2 text-center sm:space-y-3">
                {staticContent ? (
                  <>
                    {!isInstallationCheck && (
                      <TitleTag
                        ref={titleRef}
                        tabIndex={layout.titleTabIndex}
                        className={titleClassName}
                      >
                        {formatPunctuation(staticContent.initialScreen.title)}
                      </TitleTag>
                    )}
                    <p className={subtitleClassName}>
                      {staticContent.initialScreen.subtitle}
                    </p>
                  </>
                ) : (
                  <>
                    <RichTextRenderer blocks={initialTitle} className="text-3xl sm:text-4xl" />
                    <RichTextRenderer blocks={initialSubtitle} className="text-md sm:text-2xl" />
                  </>
                )}
              </div>
              {/* Wait for the session check (root-layout UserProvider) to settle so
                  logged-in members never see the signup form flash. */}
              {screenToShow === ScreenToShowState.INITIAL && isInitialized && (
                <EmailForm
                  setNewCustomerEmail={setNewCustomerEmail}
                  setExistingCustomerEmail={setExistingCustomerEmail}
                  setScreenToShow={setScreenToShow}
                  buttonText={staticContent?.initialScreen.buttonText}
                  onEmailSubmit={onEmailSubmit}
                  trackingPrefix={trackingPrefix}
                />
              )}
              {screenToShow === ScreenToShowState.NEW_CUSTOMER && (
                <NameForm
                  email={newCustomerEmail}
                  setScreenToShow={setScreenToShow}
                  buttonText={staticContent?.newAccountScreen.buttonText}
                  onCreateNewAccount={onCreateNewAccount}
                  nag={nag}
                  trackingPrefix={trackingPrefix}
                />
              )}
              {screenToShow === ScreenToShowState.LOGGED_IN && (
                <LoggedInForm
                  setScreenToShow={setScreenToShow}
                  existingCustomerEmail={existingCustomerEmail}
                  buttonText={staticContent?.existingAccountScreen.buttonText}
                  onExistingUserJoinClub={onExistingUserJoinClub}
                  nag={nag}
                  trackingPrefix={trackingPrefix}
                />
              )}
            </div>
          )}
        </Container>
      </div>
    </NukiDevicesProvider>
  );
}
